import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, ChevronLeft, Mic, Trash2, X } from 'lucide-react';
import sttService from '../services/sttService';
import { getPatientById, getPatientDisplayName } from '../constants/mockPatients';
import { patientGeneratingPath, ROUTES } from '../constants/routes';
import { transcriptHasClinicalSignal } from '../utils/clinicalContent';
import { useSession } from './SessionContext';
import { useLanguage } from './LanguageContext';
import { useUsage } from './UsageContext';

const HoldToRecordContext = createContext(null);
const AUDIO_BUFFER_SIZE = 2048;
const MIN_RECORDING_MS = 1200;
const MAX_RECORDING_MS = 180000;
const RECORDING_TOO_LONG = 'Recording too long. Maximum recording length is 3 minutes.';
const TOO_MANY_REQUESTS = 'Too many requests. Please wait a moment and try again.';
/** Swipe left this far (px) while holding to arm discard. */
const DISCARD_SWIPE_PX = 72;

function sttLimitMessage(err) {
  const detail = String(err?.message || err?.error?.detail || '');
  if (err?.status === 400 || detail.includes('Recording too long')) return RECORDING_TOO_LONG;
  if (err?.status === 429 || detail.includes('Too many requests')) return TOO_MANY_REQUESTS;
  return null;
}

export function HoldToRecordProvider({ children }) {
  const navigate = useNavigate();
  const { setIsRecordingActive, admittedPatients, handoverRecordingTarget, clearHandoverRecordingTarget } = useSession();
  const { t, language } = useLanguage();
  const usage = useUsage();

  const [overlayOpen, setOverlayOpen] = useState(false);
  const [patientId, setPatientId] = useState(null);
  const [patientLabel, setPatientLabel] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [finalTranscript, setFinalTranscript] = useState('');
  const [interimTranscript, setInterimTranscript] = useState('');
  const [error, setError] = useState(null);
  const [discardArmed, setDiscardArmed] = useState(false);

  const streamRef = useRef(null);
  const audioContextRef = useRef(null);
  const processorRef = useRef(null);
  const sourceRef = useRef(null);
  const sampleRateRef = useRef(16000);
  const isMountedRef = useRef(true);
  const holdingRef = useRef(false);
  const sessionActiveRef = useRef(false);
  const connectingRef = useRef(false);
  const finishingRef = useRef(false);
  const patientIdRef = useRef(null);
  const patientLabelRef = useRef(null);
  const inlineTranscriptHandlerRef = useRef(null);
  const recordingStartedAtRef = useRef(null);
  const liveTranscriptRef = useRef(null);
  const pointerStartXRef = useRef(null);
  const discardArmedRef = useRef(false);
  const maxDurationTimerRef = useRef(null);
  /** Transcript for the current press only — never reuse a previous session. */
  const sessionTranscriptRef = useRef({ final: '', interim: '' });
  const sessionArmedRef = useRef(false);

  const cleanupAudio = useCallback(() => {
    if (maxDurationTimerRef.current) {
      clearTimeout(maxDurationTimerRef.current);
      maxDurationTimerRef.current = null;
    }
    if (processorRef.current) {
      processorRef.current.disconnect();
      processorRef.current = null;
    }
    if (sourceRef.current) {
      sourceRef.current.disconnect();
      sourceRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
  }, []);

  const teardownSession = useCallback(() => {
    // End recognition on the open socket (EndOfStream + re-arm). Do NOT close WS.
    sttService.stop();
    cleanupAudio();
    sessionActiveRef.current = false;
    connectingRef.current = false;
    if (isMountedRef.current) {
      setIsRecording(false);
      setIsRecordingActive(false);
    }
  }, [cleanupAudio, setIsRecordingActive]);

  const resetSwipeState = useCallback(() => {
    pointerStartXRef.current = null;
    discardArmedRef.current = false;
    if (isMountedRef.current) {
      setDiscardArmed(false);
    }
  }, []);

  const closeOverlay = useCallback(() => {
    holdingRef.current = false;
    finishingRef.current = false;
    resetSwipeState();
    teardownSession();
    setOverlayOpen(false);
    setError(null);
    setFinalTranscript('');
    setInterimTranscript('');
    sessionTranscriptRef.current = { final: '', interim: '' };
    sessionArmedRef.current = false;
    setPatientId(null);
    patientIdRef.current = null;
    setPatientLabel(null);
    patientLabelRef.current = null;
    inlineTranscriptHandlerRef.current = null;
  }, [resetSwipeState, teardownSession]);

  const showOverlayError = useCallback((message, options = {}) => {
    const nextPatientId = options.patientId ?? null;
    const nextPatientLabel = options.patientLabel ?? null;
    holdingRef.current = false;
    finishingRef.current = false;
    resetSwipeState();
    teardownSession();
    setIsRecording(false);
    setIsRecordingActive(false);
    setFinalTranscript('');
    setInterimTranscript('');
    sessionTranscriptRef.current = { final: '', interim: '' };
    sessionArmedRef.current = false;
    inlineTranscriptHandlerRef.current = null;
    patientIdRef.current = nextPatientId;
    patientLabelRef.current = nextPatientLabel;
    setPatientId(nextPatientId);
    setPatientLabel(nextPatientLabel);
    setError(message || null);
    setOverlayOpen(true);
  }, [resetSwipeState, setIsRecordingActive, teardownSession]);

  const discardRecording = useCallback(() => {
    if (finishingRef.current) return;
    finishingRef.current = true;
    const durationMs = recordingStartedAtRef.current
      ? Date.now() - recordingStartedAtRef.current
      : 0;
    const audioDurationSeconds = Math.max(0, Math.round(durationMs / 1000));
    import('../services/persistClinical').then(({ persistDiscardedRecordingStub }) => {
      persistDiscardedRecordingStub({
        audioDurationSeconds,
        recordingEndAt: new Date().toISOString(),
      });
    });
    holdingRef.current = false;
    recordingStartedAtRef.current = null;
    resetSwipeState();
    setError(null);
    teardownSession();
    setOverlayOpen(false);
    setFinalTranscript('');
    setInterimTranscript('');
    sessionTranscriptRef.current = { final: '', interim: '' };
    sessionArmedRef.current = false;
    setPatientId(null);
    patientIdRef.current = null;
    setPatientLabel(null);
    patientLabelRef.current = null;
    inlineTranscriptHandlerRef.current = null;
    // Keep finishingRef true until the next press so late async STT errors
    // from the discarded session cannot flash "Transcription error".
  }, [resetSwipeState, teardownSession]);

  const finishRecording = useCallback(() => {
    if (finishingRef.current) return;
    finishingRef.current = true;
    resetSwipeState();

    // Prefer this-press transcript only. Falling back to the STT singleton
    // used to re-send the previous recording when the user released before
    // start() cleared that singleton.
    const sessionParts = [
      sessionTranscriptRef.current.final,
      sessionTranscriptRef.current.interim,
    ].filter(Boolean);
    const transcript = sessionParts.join(' ').trim();

    const trimmedTranscript = transcript.trim();
    const recordingDurationMs = recordingStartedAtRef.current
      ? Date.now() - recordingStartedAtRef.current
      : 0;
    const tooShort = recordingDurationMs < MIN_RECORDING_MS;
    if (recordingDurationMs > MAX_RECORDING_MS) {
      teardownSession();
      setOverlayOpen(true);
      setError(RECORDING_TOO_LONG);
      finishingRef.current = false;
      recordingStartedAtRef.current = null;
      return;
    }
    const nonClinical = trimmedTranscript
      ? !transcriptHasClinicalSignal(trimmedTranscript)
      : true;
    const wordCount = trimmedTranscript.split(/\s+/).filter(Boolean).length;
    const strongEnoughForShortRecording =
      wordCount >= 3 || /\d/.test(trimmedTranscript) || /[\u0600-\u06FF]{2,}/.test(trimmedTranscript);

    const pid = patientIdRef.current;
    const inlineHandler = inlineTranscriptHandlerRef.current;
    teardownSession();
    setOverlayOpen(false);
    recordingStartedAtRef.current = null;
    sessionTranscriptRef.current = { final: '', interim: '' };
    sessionArmedRef.current = false;

    const canProceed =
      trimmedTranscript
      && !nonClinical
      && (!tooShort || strongEnoughForShortRecording);

    if (canProceed) {
      if (inlineHandler) {
        inlineTranscriptHandlerRef.current = null;
        inlineHandler(trimmedTranscript, {
          recordingDurationSeconds: Math.max(1, Math.round(recordingDurationMs / 1000)),
        });
        setFinalTranscript('');
        setInterimTranscript('');
        setPatientId(null);
        patientIdRef.current = null;
        setPatientLabel(null);
        patientLabelRef.current = null;
        return;
      }
      if (handoverRecordingTarget?.patientKey) {
        const target = handoverRecordingTarget;
        clearHandoverRecordingTarget();
        navigate(ROUTES.SHIFT_HANDOVER_VOICE_GENERATING, {
          state: {
            transcript: trimmedTranscript,
            patientKey: target.patientKey,
            recordingDurationSeconds: Math.max(1, Math.round(recordingDurationMs / 1000)),
          },
        });
      } else {
        const generatingPath = pid ? patientGeneratingPath(pid) : ROUTES.GENERATING;
        navigate(generatingPath, {
          state: {
            transcript: trimmedTranscript,
            encounterDateTime: new Date().toISOString(),
            recordingDurationSeconds: Math.max(1, Math.round(recordingDurationMs / 1000)),
          },
        });
      }
      setFinalTranscript('');
      setInterimTranscript('');
      setPatientId(null);
      patientIdRef.current = null;
      setPatientLabel(null);
      patientLabelRef.current = null;
    } else if (isMountedRef.current) {
      setError(
        !trimmedTranscript || tooShort ? t('noSpeechDetected') : t('noClinicalContent')
      );
      setOverlayOpen(true);
      finishingRef.current = false;
    }
  }, [
    clearHandoverRecordingTarget,
    handoverRecordingTarget,
    navigate,
    resetSwipeState,
    t,
    teardownSession,
  ]);

  const startSession = useCallback(async () => {
    if (sessionActiveRef.current || connectingRef.current || !holdingRef.current) return;

    if (usage?.refreshUsage) {
      const snap = await usage.refreshUsage();
      if (snap?.block_recording) {
        connectingRef.current = false;
        setIsRecording(false);
        setIsRecordingActive(false);
        setError(
          "Your hospital has used all included recording hours this month. Please contact your administrator."
        );
        setOverlayOpen(true);
        return;
      }
    }

    setError(null);
    connectingRef.current = true;
    sessionTranscriptRef.current = { final: '', interim: '' };
    sessionArmedRef.current = true;
    setFinalTranscript('');
    setInterimTranscript('');
    sttService.clearTranscript?.();
    // Show recording UI immediately — never a "Connecting..." state.
    setIsRecording(true);
    setIsRecordingActive(true);
    recordingStartedAtRef.current = Date.now();
    if (maxDurationTimerRef.current) clearTimeout(maxDurationTimerRef.current);
    maxDurationTimerRef.current = setTimeout(() => {
      if (!isMountedRef.current) return;
      holdingRef.current = false;
      finishingRef.current = true;
      teardownSession();
      setError(RECORDING_TOO_LONG);
      setOverlayOpen(true);
      setIsRecording(false);
      setIsRecordingActive(false);
      recordingStartedAtRef.current = null;
    }, MAX_RECORDING_MS);

    // Kick socket warm in the background — do NOT await whenWarm().
    // whenWarm can sit for TIMEOUT_MS (8s) on a dead/stale socket; awaiting it
    // made mobile appear "recording" while dropping/wiping early speech.
    sttService.warm?.().catch(() => {
      sttService.prefetchJWT?.().catch(() => {});
    });

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
        },
      });

      if (!isMountedRef.current || !holdingRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        connectingRef.current = false;
        teardownSession();
        return;
      }

      streamRef.current = stream;
      sessionActiveRef.current = true;

      const audioContext = new AudioContext();
      audioContextRef.current = audioContext;
      sampleRateRef.current = audioContext.sampleRate;
      if (audioContext.state === 'suspended') {
        await audioContext.resume();
      }

      if (!isMountedRef.current || !holdingRef.current) {
        cleanupAudio();
        sessionActiveRef.current = false;
        connectingRef.current = false;
        teardownSession();
        return;
      }

      const source = audioContext.createMediaStreamSource(stream);
      sourceRef.current = source;

      const processor = audioContext.createScriptProcessor(AUDIO_BUFFER_SIZE, 1, 1);
      processorRef.current = processor;

      sttService.beginMicCapture?.();

      processor.onaudioprocess = (event) => {
        if (!sessionActiveRef.current) return;
        const inputData = event.inputBuffer.getChannelData(0);
        sttService.sendAudio(new Float32Array(inputData).buffer);
      };

      const mute = audioContext.createGain();
      mute.gain.value = 0;
      source.connect(processor);
      processor.connect(mute);
      mute.connect(audioContext.destination);

      if (!isMountedRef.current || !holdingRef.current) {
        teardownSession();
        setFinalTranscript('');
        setInterimTranscript('');
        setOverlayOpen(false);
        recordingStartedAtRef.current = null;
        // Do not clear finishingRef — discard may have set it to block late errors.
        return;
      }

      await sttService.start({
        sampleRate: sampleRateRef.current,
        onConnected: () => {
          if (!isMountedRef.current) return;
          connectingRef.current = false;
          if (!holdingRef.current) {
            finishRecording();
          }
        },
        onTranscript: ({ interim, final }) => {
          if (!isMountedRef.current) return;
          if (!sessionArmedRef.current) return;
          sessionTranscriptRef.current = {
            final: final || '',
            interim: interim || '',
          };
          setInterimTranscript(interim);
          setFinalTranscript(final);
        },
        onError: (err) => {
          if (!isMountedRef.current || finishingRef.current || !holdingRef.current) return;
          // Only surface hard failures (warm reconnect handles soft drops silently).
          const offline =
            (typeof navigator !== 'undefined' && navigator.onLine === false)
            || /failed to fetch|networkerror|err_internet|offline/i.test(String(err?.message || ''));
          const limitMessage = sttLimitMessage(err);
          setError(
            limitMessage
              || (offline ? t('connectionError') : t('transcriptionError'))
          );
          connectingRef.current = false;
          teardownSession();
        },
        onDisconnected: (partialTranscript) => {
          if (!isMountedRef.current || finishingRef.current) return;
          if (partialTranscript && sessionArmedRef.current) {
            sessionTranscriptRef.current = {
              final: partialTranscript,
              interim: '',
            };
            setFinalTranscript(partialTranscript);
          }
          teardownSession();
        },
      });
      connectingRef.current = false;
    } catch (err) {
      if (!isMountedRef.current || finishingRef.current || !holdingRef.current) return;
      setError(
        sttLimitMessage(err)
          || (err?.name === 'NotAllowedError' ? t('micDenied') : t('transcriptionError'))
      );
      connectingRef.current = false;
      cleanupAudio();
      sessionActiveRef.current = false;
      setIsRecording(false);
      setIsRecordingActive(false);
      recordingStartedAtRef.current = null;
    }
  }, [
    cleanupAudio,
    finishRecording,
    setIsRecordingActive,
    t,
    teardownSession,
    usage,
  ]);

  const getHoldHandlers = useCallback(
    (forPatientId = null, options = {}) => ({
      onPointerDown: (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.currentTarget.setPointerCapture(e.pointerId);
        holdingRef.current = true;
        finishingRef.current = false;
        pointerStartXRef.current = e.clientX;
        discardArmedRef.current = false;
        setDiscardArmed(false);
        patientIdRef.current = forPatientId;
        patientLabelRef.current = options.patientLabel || null;
        inlineTranscriptHandlerRef.current = options.onTranscript || null;
        sessionTranscriptRef.current = { final: '', interim: '' };
        sessionArmedRef.current = true;
        setFinalTranscript('');
        setInterimTranscript('');
        sttService.clearTranscript?.();
        setPatientId(forPatientId);
        setPatientLabel(options.patientLabel || null);
        setOverlayOpen(true);
        if (!sessionActiveRef.current && !connectingRef.current) {
          startSession();
        }
      },
      onPointerMove: (e) => {
        if (!holdingRef.current || pointerStartXRef.current == null) return;
        const deltaX = e.clientX - pointerStartXRef.current;
        const armed = deltaX <= -DISCARD_SWIPE_PX;
        if (armed !== discardArmedRef.current) {
          discardArmedRef.current = armed;
          setDiscardArmed(armed);
        }
      },
      onPointerUp: (e) => {
        e.stopPropagation();
        holdingRef.current = false;
        const shouldDiscard = discardArmedRef.current;
        resetSwipeState();
        if (shouldDiscard) {
          discardRecording();
          return;
        }
        if (sessionActiveRef.current || connectingRef.current || isRecording) {
          finishRecording();
        } else {
          setOverlayOpen(false);
        }
      },
      onPointerCancel: () => {
        holdingRef.current = false;
        const shouldDiscard = discardArmedRef.current;
        resetSwipeState();
        if (shouldDiscard) {
          discardRecording();
          return;
        }
        if (sessionActiveRef.current || connectingRef.current || isRecording) {
          finishRecording();
        } else {
          closeOverlay();
        }
      },
      onContextMenu: (e) => e.preventDefault(),
    }),
    [
      closeOverlay,
      discardRecording,
      finishRecording,
      isRecording,
      resetSwipeState,
      startSession,
    ]
  );

  useEffect(() => {
    isMountedRef.current = true;
    sttService.warm?.().catch(() => {
      sttService.prefetchJWT?.().catch(() => {});
    });
    return () => {
      isMountedRef.current = false;
      holdingRef.current = false;
      teardownSession();
    };
  }, [teardownSession]);

  const patient = patientId ? getPatientById(patientId, admittedPatients) : null;
  const patientName =
    patientLabel || (patient ? getPatientDisplayName(patient, language) : null);
  const fullDisplay = `${finalTranscript}${interimTranscript ? ` ${interimTranscript}` : ''}`.trim();

  useEffect(() => {
    const node = liveTranscriptRef.current;
    if (!node || !fullDisplay) return;
    node.scrollTop = node.scrollHeight;
  }, [fullDisplay]);

  const value = {
    overlayOpen,
    isRecording,
    patientId,
    getHoldHandlers,
    closeOverlay,
    showOverlayError,
    discardArmed,
  };

  const hintText = discardArmed
    ? t('releaseToDiscard')
    : isRecording
      ? t('releaseToFinish')
      : t('holdToRecord');

  return (
    <HoldToRecordContext.Provider value={value}>
      {children}

      {overlayOpen && (
        <div className="fixed inset-0 z-[220] flex flex-col bg-[#464E59]">
          <div className="flex shrink-0 justify-end px-4 pt-4">
            <button
              type="button"
              onClick={closeOverlay}
              className="flex h-10 w-10 items-center justify-center text-white/90"
              aria-label={t('cancelRecording')}
            >
              <X className="h-6 w-6" strokeWidth={2} />
            </button>
          </div>

          {patientName && (
            <p className="shrink-0 px-4 text-center text-lg font-medium text-white/90" dir="auto">
              {patientName}
            </p>
          )}

          {error && !isRecording && (
            <p className="mx-4 mt-2 shrink-0 rounded-xl bg-red-500/20 px-4 py-3 text-center text-sm text-red-100">
              {error}
            </p>
          )}

          <div className="flex min-h-0 flex-1 flex-col px-4 pb-40 pt-4">
            {isRecording && fullDisplay ? (
              <div
                ref={liveTranscriptRef}
                className="flex min-h-0 flex-1 flex-col overflow-y-auto px-2"
              >
                <p
                  dir="auto"
                  className="my-auto w-full max-w-md self-center text-center text-base leading-relaxed text-white"
                >
                  {finalTranscript}{' '}
                  {interimTranscript && (
                    <span className="italic text-white/50">{interimTranscript}</span>
                  )}
                </p>
              </div>
            ) : (
              <div className="flex min-h-0 flex-1 items-center justify-center" />
            )}

            {isRecording && error && (
              <p className="mt-3 shrink-0 rounded-xl bg-red-500/20 px-4 py-3 text-center text-sm text-red-100">
                {error}
              </p>
            )}
          </div>

          <p className="record-hint-bottom fixed left-0 right-0 z-[225] px-4 text-center text-sm text-white/80">
            {hintText}
          </p>

          <div className="pointer-events-none fixed inset-x-0 z-[230] fab-bottom">
            <div className="relative mx-auto flex h-[88px] w-full max-w-sm items-center px-4">
              {/* Discard (left) — outline only, not red */}
              <div
                className={`absolute left-4 top-1/2 flex -translate-y-1/2 flex-col items-center gap-1 transition-transform duration-150 ${
                  discardArmed ? 'scale-110' : 'scale-100'
                }`}
                aria-hidden="true"
              >
                <Trash2 className="h-7 w-7 text-white" strokeWidth={1.75} />
                <span className="text-[11px] font-medium text-white/85">{t('discard')}</span>
              </div>

              {/* Swipe chevrons between trash and mic */}
              <div
                className="absolute left-[4.75rem] top-1/2 flex -translate-y-1/2 items-center"
                aria-hidden="true"
              >
                <ChevronLeft
                  className={`h-5 w-5 ${discardArmed ? 'text-white/90' : 'text-white/35'}`}
                  strokeWidth={2.5}
                />
                <ChevronLeft
                  className={`-ml-3 h-5 w-5 ${discardArmed ? 'text-white/60' : 'text-white/25'}`}
                  strokeWidth={2.5}
                />
              </div>

              {/* Mic — fixed center, does not move */}
              <div className="pointer-events-auto absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                <HoldToRecordButton
                  patientId={patientId}
                  size="fab"
                  displayOnly
                  className="!relative !left-auto !bottom-auto !translate-x-0"
                />
              </div>

              {/* Done — disabled while recording */}
              <div
                className="absolute right-4 top-1/2 flex -translate-y-1/2 flex-col items-center gap-1 opacity-35"
                aria-hidden="true"
              >
                <Check className="h-7 w-7 text-white" strokeWidth={1.75} />
                <span className="text-[11px] font-medium text-white">{t('done')}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </HoldToRecordContext.Provider>
  );
}

export function useHoldToRecord() {
  const ctx = useContext(HoldToRecordContext);
  if (!ctx) throw new Error('useHoldToRecord must be used within HoldToRecordProvider');
  return ctx;
}

export function HoldToRecordButton({
  patientId = null,
  size = 'fab',
  className = '',
  displayOnly = false,
  holdOptions = null,
  anchor = 'viewport',
  dataTour = null,
}) {
  const { getHoldHandlers, isRecording, overlayOpen, discardArmed } = useHoldToRecord();
  const usage = useUsage();
  const { t } = useLanguage();
  const handlers = usage?.blockMic ? {} : getHoldHandlers(patientId, holdOptions || {});

  const isFab = size === 'fab';
  const isActive = overlayOpen && isRecording;
  const isInline = anchor === 'inline';

  return (
    <button
      type="button"
      data-tour={dataTour || undefined}
      {...(displayOnly ? {} : handlers)}
      aria-hidden={displayOnly || undefined}
      aria-label={
        displayOnly
          ? undefined
          : discardArmed
            ? t('releaseToDiscard')
            : isRecording
              ? t('releaseToFinish')
              : t('holdToRecord')
      }
      style={{ touchAction: 'none' }}
      className={`${usage?.blockMic ? 'pointer-events-none opacity-40 ' : ''}${
        isFab
          ? `${
              isInline
                ? 'relative flex touch-none select-none items-center justify-center'
                : 'fixed left-1/2 z-[100] flex -translate-x-1/2 touch-none select-none items-center justify-center fab-bottom'
            } bg-primary text-white shadow-fab transition-all active:scale-95 ${
              isActive ? 'h-[72px] w-[72px] rounded-2xl' : 'h-[72px] w-[72px] rounded-full'
            } ${className}`
          : `flex touch-none select-none items-center justify-center bg-primary-light text-primary transition-all active:scale-95 ${
              isActive ? 'h-9 w-9 rounded-lg bg-primary text-white' : 'h-9 w-9 rounded-full'
            } ${className}`
      }`}
    >
      {isActive && isRecording ? (
        <span className={`block rounded-sm bg-white ${isFab ? 'h-6 w-6' : 'h-3.5 w-3.5'}`} />
      ) : (
        <Mic className={isFab ? 'h-8 w-8' : 'h-4 w-4'} strokeWidth={2} />
      )}
    </button>
  );
}
